import React, { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, Mail, Star, Map, Globe, X, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { FaTelegram } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

function ContactUsPage() {
  const navigate = useNavigate();
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="relative min-h-screen bg-black text-white overflow-visible">
      <div
        className="cursor-glow"
        style={{
          left: `${cursorPosition.x}px`,
          top: `${cursorPosition.y}px`,
        }}
      />
      
      <div className="relative z-10">
        <nav className="fixed top-0 w-full px-6 py-4 bg-black bg-opacity-70 backdrop-blur-sm z-50">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white hover:text-green-400 transition-colors duration-300"
          >
            <ArrowLeft size={20} />
            Back to Main Page
          </button>
        </nav>

        <main className="min-h-screen bg-black pt-24 pb-12">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              {/* Hero Section */}
              <div className="text-center mb-16">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-wider text-green-400 mb-6">
                  Get in Touch With Us
                </h1>
                <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
                  We'd love to hear from you. Whether you have a project idea, a question about our services, or just want to say hello, fill out the form or connect with us through social media.
                </p>
              </div>

              {/* Contact Form and Info Grid */}
              <div className="max-w-3xl mx-auto space-y-12 mb-16">

                {/* Contact Information */}
                <div className="text-center">
                  <h2 className="text-2xl font-semibold text-white mb-8">Contact Information</h2>
                  <div className="space-y-8">
                    <div className="text-center">
                      <p className="text-gray-300 text-sm mb-1">Email</p>
                      <a 
                        href="mailto:EscapingtheMatrixAI@proton.me" 
                        className="text-white text-lg hover:text-green-400 transition-colors duration-300"
                      >
                        EscapingtheMatrixAI@proton.me
                      </a>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-300 text-sm mb-1">Location</p>
                      <p className="text-white text-lg">Grande Prairie, Alberta, Canada</p>
                    </div>
                  </div>
                </div>

                {/* Social Media Links */}
                <div className="text-center">
                  <h2 className="text-2xl font-semibold text-white mb-8">Connect With Us</h2>
                  <div className="flex flex-col items-center space-y-4">
                    <a
                      href="https://escapingthematrix.io"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-4 p-4 w-full max-w-xs rounded-lg bg-black/50 hover:bg-green-400/10 transition-colors duration-300 group"
                    >
                      <Globe className="w-8 h-8 text-green-400 group-hover:text-green-300" />
                      <span className="text-white text-lg group-hover:text-green-400">Website</span>
                    </a>
                    <a
                      href="https://x.com/EscapeMatrixAI"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-4 p-4 w-full max-w-xs rounded-lg bg-black/50 hover:bg-green-400/10 transition-colors duration-300 group"
                    >
                      <X className="w-8 h-8 text-white group-hover:text-gray-400" />
                      <span className="text-white text-lg group-hover:text-gray-400">Twitter/X</span>
                    </a>
                    <a
                      href="https://www.linkedin.com/company/escaping-the-matrix"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-4 p-4 w-full max-w-xs rounded-lg bg-black/50 hover:bg-green-400/10 transition-colors duration-300 group"
                    >
                      <Linkedin className="w-8 h-8 text-[#0077B5] group-hover:text-[#0077B5]" />
                      <span className="text-white text-lg group-hover:text-[#0077B5]">LinkedIn</span>
                    </a>
                    <a
                      href="https://t.me/AIEscapingtheMatrix"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-4 p-4 w-full max-w-xs rounded-lg bg-black/50 hover:bg-green-400/10 transition-colors duration-300 group"
                    >
                      <FaTelegram className="w-8 h-8 text-[#0088CC] group-hover:text-[#0088CC]" />
                      <span className="text-white text-lg group-hover:text-[#0088CC]">Telegram</span>
                    </a>
                    <a
                      href="#"
                      className="flex items-center justify-center gap-4 p-4 w-full max-w-xs rounded-lg bg-black/50 opacity-60 cursor-not-allowed"
                    >
                      <Facebook className="w-8 h-8 text-[#1877F2]" />
                      <span className="text-gray-500 text-lg">Facebook - Coming Soon</span>
                    </a>
                    <a
                      href="#"
                      className="flex items-center justify-center gap-4 p-4 w-full max-w-xs rounded-lg bg-black/50 opacity-60 cursor-not-allowed"
                    >
                      <Instagram className="w-8 h-8 text-[#E1306C]" />
                      <span className="text-gray-500 text-lg">Instagram - Coming Soon</span>
                    </a>
                    <a
                      href="#"
                      className="flex items-center justify-center gap-4 p-4 w-full max-w-xs rounded-lg bg-black/50 opacity-60 cursor-not-allowed"
                    >
                      <Youtube className="w-8 h-8 text-[#FF0000]" />
                      <span className="text-gray-500 text-lg">YouTube - Coming Soon</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="fixed bottom-0 w-full px-6 py-4 bg-black bg-opacity-70 backdrop-blur-sm">
          <div className="flex justify-between">
            <div className="flex space-x-6">
              <a href="https://x.com/EscapeMatrixAI" target="_blank" rel="noopener noreferrer" className="nav-link">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-current">
                  <path d="M16.99 0H20.298L13.071 8.26L21.573 19.5H14.916L9.702 12.683L3.736 19.5H0.426L8.156 10.665L0 0H6.826L11.539 6.231L16.99 0ZM15.829 17.52H17.662L5.83 1.876H3.863L15.829 17.52Z" fill="currentColor"/>
                </svg>
              </a>
              <a href="https://t.me/AIEscapingtheMatrix" target="_blank" rel="noopener noreferrer" className="nav-link">
                <FaTelegram size={18} />
              </a>
              <a href="mailto:EscapingtheMatrixAI@proton.me" className="nav-link">
                <Mail size={18} />
              </a>
              <a href="https://sitemap.escapingthematrix.io/" target="_blank" rel="noopener noreferrer" className="nav-link">
                <Map size={18} />
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default ContactUsPage;